import type { AdminPayoutStatus } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Payout status badge chip for the queue + detail drawer (AUT-128).
 * Colours mirror the mockup's `badge--{danger,warning,success,neutral}`
 * classes via MUI Chip `color`. Labels resolve through i18n
 * (`payouts.status.<value>`; CLAUDE.md rule #11).
 *
 * Typed as a total `Record` over `AdminPayoutStatus` PLUS `'superseded'`:
 * the LIST wire only carries the three triage statuses, but the DETAIL
 * wire's `status` widens to include `'superseded'` (the retry-internal
 * marker that surfaces on the OLD row). Covering it here means the drawer
 * header renders a coloured chip instead of crashing on an unmapped value.
 *
 * Mapping (mockup-aligned):
 *   - failed     → error    "ошибка"
 *   - pending    → warning  "в обработке"
 *   - completed  → success  "завершена"
 *   - superseded → default  "заменена" (retry-internal, OLD row only)
 */
type PayoutStatusWithSuperseded = AdminPayoutStatus | 'superseded';

type ChipColor = ChipProps['color'];

const STATUS_COLOR: Record<PayoutStatusWithSuperseded, ChipColor> = {
  failed: 'error',
  pending: 'warning',
  completed: 'success',
  superseded: 'default',
};

export const PayoutStatusBadge = ({
  status,
}: {
  status: PayoutStatusWithSuperseded;
}): ReactElement => {
  const { t } = useTranslation();
  return <Chip size="small" color={STATUS_COLOR[status]} label={t(`payouts.status.${status}`)} />;
};
